import { Component, h, Listen, State } from '@stencil/core';
import Autocomplete from '@tarekraafat/autocomplete.js';
import { setupAutocomplete } from '@solid-design-system/components/dist/utilities/autocomplete-config';
import store from '../../../global/store';
import { tracker } from '../../../global/tracker';

type TriggeredBy = 'Button' | 'Enter' | 'AutocompleteEntry';

@Component({
  tag: 'ui-bank-search-autocomplete',
  styleUrl: 'ui-bank-search-autocomplete.scss',
})
export class UiBankSearchAutocomplete {
  private inputComponent: HTMLInputElement & { updateComplete: Promise<void> };

  private autocomplete: Autocomplete;

  private AutocompleteService: google.maps.places.AutocompleteService = null;

  private PlacesService: google.maps.places.PlacesService = null;

  @State() inAutocompleteList = false;

  // A unique id is required to support multiple instances of the component on the same page
  private uniqueId: string = `bank-search-autocomplete-${crypto.randomUUID()}`;

  async componentDidLoad() {
    await this.inputComponent.updateComplete;
    if (store.state.google) {
      // Depending on the users history, google might already be available
      this.initGoogleServicesAndAutocomplete();
    }
    store.onChange('google', () => {
      // If google is not available, we need to wait for it to be loaded
      // Only needed if the services are not already initialized
      if (!this.AutocompleteService || !this.PlacesService) {
        this.initGoogleServicesAndAutocomplete();
      }
    });
  }

  private initGoogleServicesAndAutocomplete() {
    this.AutocompleteService =
      new store.state.google.maps.places.AutocompleteService();
    this.PlacesService = new store.state.google.maps.places.PlacesService(
      document.createElement('div')
    );

    // Autocomplete needs the services to be initialized
    this.initAutocomplete();
  }

  private initAutocomplete() {
    Promise.all([
      customElements.whenDefined('sd-input'),
      customElements.whenDefined('sd-popup'),
    ]).then(() => {
      const { config: baseConfig } = setupAutocomplete(`#${this.uniqueId}`);
      this.autocomplete = new Autocomplete({
        ...baseConfig,
        name: this.uniqueId,
        placeHolder: 'PLZ, Ort oder Bankname',
        data: {
          src: async (query) => {
            const results = await this.AutocompleteService.getPlacePredictions({
              input: query,
              componentRestrictions: {
                country: 'de',
              },
            });
            if (results.predictions.length > 0) {
              this.handleError(false);
            }
            return results.predictions;
          },
          keys: ['description'],
        },
        threshold: 1,
        // We set searchEngine to loose since we do not want the lib to do the actual filtering.
        // The complete list should be shown that is provided by Google Maps.
        // The only reason for using the lib is to use its a11y features and keyboard navigation.
        // See https://solid-design-system.fe.union-investment.de/docs/?path=/docs/templates-autocomplete--docs
        searchEngine: 'loose',
        resultsList: {
          maxResults: undefined, // Do not remove
        },
        events: {
          input: {
            selection: async (
              e: CustomEvent<{
                selection: { value: google.maps.places.AutocompletePrediction };
              }>
            ) => {
              // First get the place details, then update the map search
              this.PlacesService.getDetails(
                {
                  placeId: e.detail.selection.value.place_id,
                },
                (place) => {
                  this.onPlaceChange(place, 'AutocompleteEntry');
                  this.inputComponent.value =
                    e.detail.selection.value.description;
                }
              );
            },
            close: () => {
              // Selecting an autocomplete entry with enter closes the popup.
              // This sets the inAutocompleteList state to false - indicating that the user is not in the autocomplete list anymore.
              // However, the keydown event is only registered after the next render (instead of before setting the state).
              // This causes the keydown event to fire. The timeout makes sure that the state persists until after the
              // keydown event is registered.
              setTimeout(() => {
                this.inAutocompleteList = false;
              }, 100);
            },
            navigate: () => {
              this.inAutocompleteList = true;
            },
          },
        },
      });
    });
  }

  private handleError(isError: boolean) {
    if (!isError) {
      this.inputComponent.setCustomValidity('');
    } else {
      this.inputComponent.setCustomValidity(
        `${(window as any).ui.services?.i18n?.translate('bankSearch').noResults || 'Ihre Anfrage lieferte kein Ergebnis'}`
      );
    }

    this.inputComponent.reportValidity();
  }

  private onPlaceChange(
    place: google.maps.places.PlaceResult,
    triggeredBy: TriggeredBy
  ) {
    // When executing search with a term that produces no results, i.e. 'scljhwbr',
    // a place with name===searchTerm but no address_components or geometry is returned.
    // Presence of address_components and geometry indicates a valid search result.
    const isInvalidResult = !place.address_components || !place.geometry;

    if (isInvalidResult) {
      this.handleError(true);
    } else {
      this.handleError(false);
      store.state.selectedPlaceLocation = place.geometry.location;
      store.state.selectedPlaceName = place.name;

      // Tracking
      switch (triggeredBy) {
        case 'Button':
          tracker.trackInputButton();
          break;
        case 'Enter':
          tracker.trackInputEnter();
          break;
        case 'AutocompleteEntry':
          tracker.trackInputAutocompleteEntry();
          break;
        default:
          break;
      }
    }
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    // Only handle keydown if it's coming from the input element
    if (this.inAutocompleteList) {
      return;
    }
    if (event.target === this.inputComponent && event.code === 'Enter') {
      this.fireManualSearch('Enter');
    }
  }

  // This is called when the user clicks the magnifying glass icon or presses enter
  // We use the first prediction
  private fireManualSearch(triggeredBy: TriggeredBy) {
    if (!this.inputComponent.value) return;

    this.autocomplete?.close();

    this.AutocompleteService?.getPlacePredictions(
      {
        input: this.inputComponent.value,
      },
      (predictions) => {
        if (!predictions || predictions.length === 0) {
          this.handleError(true);
        } else {
          // Get place details
          this.PlacesService.getDetails(
            {
              placeId: predictions[0].place_id,
            },
            (place) => {
              this.onPlaceChange(place, triggeredBy);
            }
          );
        }
      }
    );
  }

  @Listen('sd-clear')
  handleInputClear() {
    this.handleError(false);

    // Remove search entry from store
    store.state.selectedPlaceName = '';
  }

  render() {
    return (
      <sd-input
        clearable
        id={this.uniqueId}
        type="text"
        name="search"
        label={
          (window as any).ui.services?.i18n?.translate('bankSearch')
            .searchLabel || 'Standortsuche'
        }
        ref={(el) => {
          this.inputComponent = el;
        }}
      >
        <sd-button
          slot="right"
          onClick={() => {
            this.fireManualSearch('Button');
          }}
          variant="tertiary"
        >
          <sd-icon
            library={(window as any).ui.cmsState?.iconLibrary}
            name="union-investment/system/magnifying-glass"
          ></sd-icon>
          <span class="sr-only">Suche starten</span>
        </sd-button>
        <span slot="help-text" class="sr-only">
          Nach einem Ort suchen
        </span>
      </sd-input>
    );
  }
}
